<template>
  <div class="container mx-auto mt-16 px-4 pb-20">
    <h1 class="font-bold text-4xl mb-8">
      {{ isNew ? "New recipe" : "Editing recipe" }}
    </h1>

    <FormErrors :errors="errors" subject="recipe" />

    <form
      @submit.prevent="submitForm"
      enctype="multipart/form-data"
      @focusin="handleFocusin"
    >
      <div class="flex gap-8 items-start">
        <!-- Sidebar navigation -->
        <nav class="hidden lg:block w-48 shrink-0 self-start sticky top-20">
          <div class="">
            <h3
              class="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3"
            >
              Sections
            </h3>
            <ul class="flex flex-col gap-0.5">
              <li v-for="section in sidebarSections" :key="section.id">
                <button
                  type="button"
                  @click="scrollToSection(section.id)"
                  class="w-full text-left px-3 py-1.5 rounded-md text-sm transition-colors truncate"
                  :class="
                    activeSection === section.id
                      ? 'bg-primary/10 text-primary font-medium border-l-2 border-primary'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  "
                  :title="section.label"
                >
                  {{ section.label }}
                </button>
              </li>
            </ul>
            <div class="mt-6 pt-4 border-t border-border flex flex-col gap-2">
              <Button
                type="button"
                @click="submitForm"
                :disabled="form.processing"
                class="w-full"
              >
                {{ form.processing ? "Saving…" : "Save" }}
              </Button>
              <Button
                v-if="recipePath"
                as="a"
                :href="recipePath"
                variant="outline"
                class="w-full"
              >
                View recipe
              </Button>
              <Button
                as="a"
                :href="recipePath || recipesPath"
                variant="ghost"
                class="w-full"
              >
                Cancel
              </Button>
            </div>
          </div>
        </nav>

        <!-- Main content -->
        <div class="grow min-w-0">
          <!-- Basic Info -->
          <div id="section-info" data-section="section-info">
            <h2 class="text-xl font-semibold text-gray-800 mt-2 mb-4">
              Basic Info
            </h2>
            <Card class="flex flex-col gap-0 p-6">
              <!-- Title -->
              <FormField
                label="Title"
                name="recipe[title]"
                v-model="form.title"
              />

              <!-- Ingress -->
              <FormField
                label="Ingress"
                name="recipe[ingress]"
                v-model="form.ingress"
              />

              <!-- Slug -->
              <FormField
                label="Slug (URL)"
                name="recipe[slug]"
                v-model="form.slug"
                placeholder="t.ex. kanelbullar"
              />

              <!-- State + Difficulty + Portioner -->
              <div class="flex gap-4">
                <div class="w-40 shrink-0">
                  <FormSelect
                    label="State"
                    name="recipe[state]"
                    v-model="form.state"
                  >
                    <option value="draft">Draft</option>
                    <option value="published">Published</option>
                    <option value="hidden">Hidden</option>
                  </FormSelect>
                </div>
                <div class="w-40 shrink-0">
                  <FormSelect
                    label="Svårighetsgrad"
                    name="recipe[difficulty]"
                    v-model="form.difficulty"
                  >
                    <option value="">–</option>
                    <option value="enkel">Enkel</option>
                    <option value="medel">Medel</option>
                    <option value="avancerad">Avancerad</option>
                  </FormSelect>
                </div>
                <div class="flex gap-2 items-end w-auto shrink-0">
                  <div class="w-24">
                    <FormField
                      label="Antal"
                      name="recipe[servings]"
                      type="number"
                      v-model="form.servings"
                    />
                  </div>
                  <div class="w-32">
                    <FormSelect
                      label="Enhet"
                      name="recipe[servings_unit]"
                      v-model="form.servings_unit"
                    >
                      <option value="portioner">Portioner</option>
                      <option value="bitar">Bitar</option>
                      <option value="stycken">Stycken</option>
                    </FormSelect>
                  </div>
                </div>
              </div>
            </Card>
          </div>

          <!-- Images -->
          <div id="section-images" data-section="section-images" class="mt-8">
            <h2 class="text-xl font-semibold text-gray-800 mb-4">Images</h2>
            <Card class="flex flex-col gap-4 p-6">
              <!-- Cover image -->
              <div>
                <label class="block font-medium mb-2">Cover Image</label>
                <div
                  v-if="recipe.cover_image"
                  class="flex items-center gap-4 mb-3"
                >
                  <img
                    :src="recipe.cover_image.small"
                    alt="Current cover image"
                    class="w-24 h-24 object-cover rounded-lg shadow"
                  />
                  <p class="text-sm text-gray-500">
                    Current image. Upload a new file to replace it.
                  </p>
                </div>
                <input
                  type="file"
                  name="recipe[cover_image]"
                  accept="image/*"
                  class="block"
                  @change="form.cover_image = $event.target.files[0]"
                />
              </div>

              <hr class="border-gray-200" />

              <!-- Gallery images -->
              <div>
                <label class="block font-medium mb-2">Gallery Images</label>
                <input
                  type="file"
                  name="recipe[images][]"
                  accept="image/*"
                  multiple
                  class="block"
                  @change="form.images = Array.from($event.target.files)"
                />
              </div>
            </Card>
          </div>

          <!-- Timing -->
          <div id="section-timing" data-section="section-timing" class="mt-8">
            <h2 class="text-xl font-semibold text-gray-800 mb-4">Timing</h2>
            <Card class="p-6">
              <div class="flex gap-6">
                <div class="grow">
                  <FormField
                    label="Prep time (minutes)"
                    name="recipe[prep_time]"
                    type="number"
                    v-model="form.prep_time"
                  />
                </div>
                <div class="grow">
                  <FormField
                    label="Cook time (minutes)"
                    name="recipe[cook_time]"
                    type="number"
                    v-model="form.cook_time"
                  />
                </div>
              </div>
            </Card>
          </div>

          <!-- Body -->
          <div id="section-body" data-section="section-body" class="mt-8">
            <h2 class="text-xl font-semibold text-gray-800 mb-4">Body</h2>
            <Card class="p-6 flex flex-col gap-4">
              <FormTextarea
                label="Body"
                name="recipe[body]"
                v-model="form.body"
              />
              <div class="w-56">
                <FormSelect
                  label="Visa efter steg"
                  name="recipe[body_after_step]"
                  v-model="form.body_after_step"
                >
                  <option :value="null">– Visa inte</option>
                  <option v-for="(step, i) in form.steps" :key="i" :value="i">
                    Steg {{ i + 1
                    }}<template v-if="step.title"> – {{ step.title }}</template>
                  </option>
                </FormSelect>
              </div>
            </Card>
          </div>

          <!-- Categories -->
          <div
            id="section-categories"
            data-section="section-categories"
            class="mt-8"
          >
            <h2 class="text-xl font-semibold text-gray-800 mb-4">Categories</h2>
            <Card class="p-6">
              <div class="flex gap-4 flex-wrap">
                <div
                  v-for="category in parentCategories"
                  :key="category.id"
                  class="flex items-center gap-2"
                >
                  <input
                    type="checkbox"
                    name="recipe[category_ids][]"
                    :value="category.id"
                    :checked="form.category_ids.includes(category.id)"
                    @change="toggleCategory(category.id)"
                  />
                  <label class="font-medium text-lg">{{ category.name }}</label>
                </div>
              </div>

              <hr class="h-px border-0 bg-gray-200 my-4" />

              <div class="flex gap-4 flex-wrap">
                <div
                  v-for="category in childCategories"
                  :key="category.id"
                  class="flex items-center gap-2"
                >
                  <input
                    type="checkbox"
                    name="recipe[category_ids][]"
                    :value="category.id"
                    :checked="form.category_ids.includes(category.id)"
                    @change="toggleCategory(category.id)"
                  />
                  <label class="font-normal">{{ category.name }}</label>
                </div>
              </div>
            </Card>
          </div>

          <!-- Linked techniques & tools -->
          <div id="section-linked" data-section="section-linked" class="mt-8">
            <h2 class="text-xl font-semibold text-gray-800 mb-4">
              Tekniker &amp; verktyg
            </h2>
            <Card class="p-6 space-y-5">
              <!-- Techniques -->
              <div>
                <p class="font-medium text-sm text-gray-700 mb-2">Tekniker</p>
                <div v-if="allTechniques?.length" class="flex gap-3 flex-wrap">
                  <div
                    v-for="t in allTechniques"
                    :key="t.id"
                    class="flex items-center gap-2"
                  >
                    <input
                      type="checkbox"
                      :value="t.id"
                      :checked="form.linked_technique_ids.includes(t.id)"
                      @change="toggleLinkedTechnique(t.id)"
                      class="accent-teal-500"
                    />
                    <label class="text-sm">{{ t.title }}</label>
                  </div>
                </div>
                <p v-else class="text-xs text-gray-400">
                  Inga publicerade tekniker ännu.
                </p>
              </div>

              <!-- Tools -->
              <div v-if="availableTools?.length">
                <p class="font-medium text-sm text-gray-700 mb-2">Verktyg</p>
                <div class="flex gap-3 flex-wrap">
                  <div
                    v-for="tool in availableTools"
                    :key="tool.key"
                    class="flex items-center gap-2"
                  >
                    <input
                      type="checkbox"
                      :value="tool.key"
                      :checked="form.linked_tool_keys.includes(tool.key)"
                      @change="toggleLinkedTool(tool.key)"
                      class="accent-teal-500"
                    />
                    <label class="text-sm">{{ tool.label }}</label>
                  </div>
                </div>
              </div>
            </Card>
          </div>

          <!-- Steps -->
          <div class="mt-8">
            <h2 class="text-xl font-semibold text-gray-800 mb-4">Steps</h2>
            <div class="flex flex-col mb-6 gap-5">
              <template
                v-for="(step, stepIndex) in form.steps"
                :key="step._key"
              >
                <!-- Hidden fields for existing steps being destroyed -->
                <template v-if="step._destroy">
                  <input
                    v-if="step.id"
                    type="hidden"
                    :name="`recipe[steps_attributes][${stepIndex}][id]`"
                    :value="step.id"
                  />
                  <input
                    type="hidden"
                    :name="`recipe[steps_attributes][${stepIndex}][_destroy]`"
                    value="1"
                  />
                </template>

                <!-- Visible step card -->
                <Card
                  v-else
                  class="flex gap-4 p-6"
                  :id="`section-step-${stepIndex}`"
                  :data-section="`section-step-${stepIndex}`"
                >
                  <input
                    v-if="step.id"
                    type="hidden"
                    :name="`recipe[steps_attributes][${stepIndex}][id]`"
                    :value="step.id"
                  />
                  <input
                    type="hidden"
                    :name="`recipe[steps_attributes][${stepIndex}][position]`"
                    :value="stepIndex"
                  />

                  <div class="grow">
                    <div class="form-group pb-4 flex gap-4">
                      <Input
                        type="text"
                        :name="`recipe[steps_attributes][${stepIndex}][title]`"
                        v-model="step.title"
                        placeholder="Step Title"
                        class="grow"
                      />
                      <button
                        type="button"
                        @click="removeStep(stepIndex)"
                        class="text-red-500 hover:text-red-700 flex items-center gap-1"
                        title="Remove step"
                      >
                        <TrashIcon />
                      </button>
                    </div>

                    <!-- Ingredient Groups -->
                    <div class="mb-6">
                      <h3
                        class="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3"
                      >
                        Ingredients
                      </h3>
                      <div class="flex flex-col gap-3">
                        <template
                          v-for="(group, groupIndex) in step.ingredient_groups"
                          :key="group._key"
                        >
                          <template v-if="group._destroy">
                            <input
                              v-if="group.id"
                              type="hidden"
                              :name="`recipe[steps_attributes][${stepIndex}][ingredient_groups_attributes][${groupIndex}][id]`"
                              :value="group.id"
                            />
                            <input
                              type="hidden"
                              :name="`recipe[steps_attributes][${stepIndex}][ingredient_groups_attributes][${groupIndex}][_destroy]`"
                              value="1"
                            />
                          </template>

                          <div
                            v-else
                            class="rounded-lg border border-gray-200 overflow-hidden"
                          >
                            <input
                              v-if="group.id"
                              type="hidden"
                              :name="`recipe[steps_attributes][${stepIndex}][ingredient_groups_attributes][${groupIndex}][id]`"
                              :value="group.id"
                            />

                            <!-- Group header -->
                            <div
                              class="bg-gray-50 border-b border-gray-200 px-4 py-2.5 flex items-center gap-2"
                            >
                              <Input
                                type="text"
                                :name="`recipe[steps_attributes][${stepIndex}][ingredient_groups_attributes][${groupIndex}][title]`"
                                v-model="group.title"
                                placeholder="Group name (e.g. Sauce, Dough…)"
                                aria-label="Ingredient group name"
                                class="grow bg-white"
                                @keydown.enter.prevent="
                                  addIngredientAndFocus(group)
                                "
                              />
                              <button
                                type="button"
                                @click="removeGroup(step, groupIndex)"
                                class="text-red-400 hover:text-red-600 shrink-0"
                                title="Remove group"
                              >
                                <TrashIcon />
                              </button>
                            </div>

                            <!-- Ingredients -->
                            <div class="px-4 py-3">
                              <!-- Column headers -->
                              <div
                                class="grid grid-cols-[1fr_80px_110px_32px] gap-2 mb-1.5 px-1"
                              >
                                <span class="text-xs font-medium text-gray-600"
                                  >Ingredient</span
                                >
                                <span class="text-xs font-medium text-gray-600"
                                  >Amount</span
                                >
                                <span class="text-xs font-medium text-gray-600"
                                  >Unit</span
                                >
                                <span></span>
                              </div>

                              <div class="flex flex-col gap-1.5">
                                <template
                                  v-for="(
                                    ingredient, ingredientIndex
                                  ) in group.ingredients"
                                  :key="ingredient._key"
                                >
                                  <template v-if="ingredient._destroy">
                                    <input
                                      v-if="ingredient.id"
                                      type="hidden"
                                      :name="`recipe[steps_attributes][${stepIndex}][ingredient_groups_attributes][${groupIndex}][ingredients_attributes][${ingredientIndex}][id]`"
                                      :value="ingredient.id"
                                    />
                                    <input
                                      type="hidden"
                                      :name="`recipe[steps_attributes][${stepIndex}][ingredient_groups_attributes][${groupIndex}][ingredients_attributes][${ingredientIndex}][_destroy]`"
                                      value="1"
                                    />
                                  </template>

                                  <div
                                    v-else
                                    class="grid grid-cols-[1fr_80px_110px_32px] gap-2 items-center"
                                  >
                                    <input
                                      v-if="ingredient.id"
                                      type="hidden"
                                      :name="`recipe[steps_attributes][${stepIndex}][ingredient_groups_attributes][${groupIndex}][ingredients_attributes][${ingredientIndex}][id]`"
                                      :value="ingredient.id"
                                    />
                                    <Input
                                      type="text"
                                      :name="`recipe[steps_attributes][${stepIndex}][ingredient_groups_attributes][${groupIndex}][ingredients_attributes][${ingredientIndex}][name]`"
                                      v-model="ingredient.name"
                                      placeholder="e.g. flour"
                                      aria-label="Ingredient name"
                                      :data-ingredient-name="ingredient._key"
                                      @keydown.enter.prevent="
                                        addIngredientAndFocus(group)
                                      "
                                    />
                                    <Input
                                      type="text"
                                      :name="`recipe[steps_attributes][${stepIndex}][ingredient_groups_attributes][${groupIndex}][ingredients_attributes][${ingredientIndex}][amount]`"
                                      v-model="ingredient.amount"
                                      placeholder="100"
                                      aria-label="Amount"
                                      @keydown.enter.prevent="
                                        addIngredientAndFocus(group)
                                      "
                                    />
                                    <select
                                      :name="`recipe[steps_attributes][${stepIndex}][ingredient_groups_attributes][${groupIndex}][ingredients_attributes][${ingredientIndex}][amount_type]`"
                                      v-model="ingredient.amount_type"
                                      aria-label="Unit"
                                      class="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-xs transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                                      tabindex="-1"
                                    >
                                      <option
                                        v-for="type in amountTypes"
                                        :key="type"
                                        :value="type"
                                      >
                                        {{ type }}
                                      </option>
                                    </select>
                                    <button
                                      type="button"
                                      @click="
                                        removeIngredient(group, ingredientIndex)
                                      "
                                      class="text-red-400 hover:text-red-600 shrink-0 flex items-center justify-center"
                                      title="Remove ingredient"
                                      tabindex="-1"
                                    >
                                      <TrashIcon />
                                    </button>
                                  </div>
                                </template>
                              </div>

                              <button
                                type="button"
                                @click="addIngredient(group)"
                                class="mt-2.5 text-sm text-primary hover:text-primary/80 font-medium flex items-center gap-1 transition-colors"
                              >
                                + Add ingredient
                              </button>
                            </div>
                          </div>
                        </template>

                        <button
                          type="button"
                          @click="addGroup(step)"
                          class="flex items-center justify-center gap-2 py-3 border border-dashed border-gray-300 rounded-lg text-sm text-gray-500 hover:border-gray-400 hover:text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer"
                        >
                          + Add ingredient group
                        </button>
                      </div>
                    </div>

                    <!-- Step instructions -->
                    <div
                      class="flex gap-2 mt-4 w-full rounded-lg border border-gray-200 p-4"
                    >
                      <div class="w-full">
                        <label
                          class="block text-sm font-medium text-gray-700 mb-1"
                          >Instructions</label
                        >
                        <textarea
                          :name="`recipe[steps_attributes][${stepIndex}][instructions]`"
                          v-model="step.instructions"
                          placeholder="Instructions"
                          class="w-full border rounded px-3 py-2 h-36"
                        ></textarea>
                      </div>
                    </div>
                  </div>
                </Card>
              </template>

              <Button type="button" @click="addStep"> Add step </Button>
            </div>
          </div>
          <!-- end section-steps -->
        </div>
      </div>
    </form>
  </div>
</template>

<script setup>
import { reactive, computed, ref, nextTick } from "vue";
import { useForm } from "@inertiajs/vue3";
import TrashIcon from "@/components/icons/TrashIcon.vue";
import FormErrors from "@/components/form/FormErrors.vue";
import FormField from "@/components/form/FormField.vue";
import FormTextarea from "@/components/form/FormTextarea.vue";
import FormSelect from "@/components/form/FormSelect.vue";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";

const props = defineProps({
  recipe: Object,
  categories: Array,
  allTechniques: { type: Array, default: () => [] },
  availableTools: { type: Array, default: () => [] },
  currentUser: Object,
  csrfToken: String,
  actionPath: String,
  formMethod: String,
  recipesPath: String,
  recipePath: String,
  errors: Array,
});

const isNew = computed(() => !props.recipe.id);

const amountTypes = ["gram", "kg", "ml", "tsk", "msk", "dl", "liter", "st"];

let keyCounter = 0;
function nextKey() {
  return `k_${++keyCounter}`;
}

function buildIngredient(data = {}) {
  return {
    _key: nextKey(),
    id: data.id || null,
    name: data.name || "",
    amount: data.amount || "",
    amount_type: data.amount_type || "gram",
    _destroy: false,
  };
}

function buildGroup(data = {}) {
  return {
    _key: nextKey(),
    id: data.id || null,
    title: data.title || "",
    ingredients: (data.ingredients || []).map(buildIngredient),
    _destroy: false,
  };
}

function buildStep(data = {}) {
  return {
    _key: nextKey(),
    id: data.id || null,
    title: data.title || "",
    position: data.position || 0,
    instructions: data.instructions || "",
    ingredient_groups: (data.ingredient_groups || []).map(buildGroup),
    _destroy: false,
  };
}

const form = useForm({
  slug: props.recipe.slug,
  title: props.recipe.title,
  ingress: props.recipe.ingress,
  body: props.recipe.body,
  prep_time: props.recipe.prep_time,
  cook_time: props.recipe.cook_time,
  servings: props.recipe.servings,
  servings_unit: props.recipe.servings_unit || "portioner",
  body_after_step: props.recipe.body_after_step ?? null,
  state: props.recipe.state || "draft",
  difficulty: props.recipe.difficulty || "",
  category_ids: [...(props.recipe.category_ids || [])],
  linked_technique_ids: [...(props.recipe.linked_technique_ids || [])],
  linked_tool_keys: [...(props.recipe.linked_tool_keys || [])],
  steps: (props.recipe.steps || []).map(buildStep),
  cover_image: null,
  images: null,
});

const parentCategories = computed(() =>
  props.categories.filter((c) => !c.parent_id),
);
const childCategories = computed(() =>
  props.categories.filter((c) => c.parent_id),
);

const activeSection = ref(null);

function serializeForRails(data) {
  const recipe = {
    slug: data.slug,
    title: data.title,
    ingress: data.ingress,
    body: data.body,
    prep_time: data.prep_time,
    cook_time: data.cook_time,
    servings: data.servings,
    servings_unit: data.servings_unit,
    body_after_step: data.body_after_step,
    state: data.state,
    difficulty: data.difficulty || null,
    // Empty string ensures category_ids param is always sent (clears selections)
    category_ids: ["", ...data.category_ids],
    linked_technique_ids: data.linked_technique_ids,
    linked_tool_keys: data.linked_tool_keys,
    steps_attributes: data.steps.map((step, si) => {
      const s = {
        title: step.title,
        position: si,
        instructions: step.instructions,
        ingredient_groups_attributes: step.ingredient_groups.map(
          (group, gi) => {
            const g = {
              title: group.title,
              ingredients_attributes: group.ingredients.map((ing) => {
                const i = {
                  name: ing.name,
                  amount: ing.amount,
                  amount_type: ing.amount_type,
                };
                if (ing.id) i.id = ing.id;
                if (ing._destroy) i._destroy = "1";
                return i;
              }),
            };
            if (group.id) g.id = group.id;
            if (group._destroy) g._destroy = "1";
            return g;
          },
        ),
      };
      if (step.id) s.id = step.id;
      if (step._destroy) s._destroy = "1";
      return s;
    }),
  };

  if (data.cover_image) recipe.cover_image = data.cover_image;
  if (data.images && data.images.length) recipe.images = data.images;

  return { recipe };
}

function submitForm() {
  const method = props.formMethod === "post" ? "post" : "patch";
  form.transform(serializeForRails)[method](props.actionPath, {
    forceFormData: true,
    preserveScroll: true,
  });
}

const visibleSteps = computed(() =>
  form.steps
    .map((step, index) => ({ step, index }))
    .filter(({ step }) => !step._destroy),
);

const sidebarSections = computed(() => {
  const sections = [
    { id: "section-info", label: "Basic Info" },
    { id: "section-images", label: "Images" },
    { id: "section-timing", label: "Timing" },
    { id: "section-body", label: "Body" },
    { id: "section-categories", label: "Categories" },
    { id: "section-linked", label: "Tekniker & verktyg" },
  ];

  visibleSteps.value.forEach(({ step, index }, visibleIndex) => {
    // id uses raw array index to match the DOM element rendered by v-for,
    // label uses visibleIndex for clean sequential numbering
    sections.push({
      id: `section-step-${index}`,
      label: step.title || `Step ${visibleIndex + 1}`,
    });
  });

  return sections;
});

const STICKY_HEADER_OFFSET = 80;

function scrollToSection(id) {
  const el = document.getElementById(id);
  if (el) {
    const top =
      el.getBoundingClientRect().top + window.scrollY - STICKY_HEADER_OFFSET;
    window.scrollTo({ top, behavior: "smooth" });
    activeSection.value = id;
  }
}

function handleFocusin(event) {
  const sectionEl = event.target.closest("[data-section]");
  if (sectionEl) {
    activeSection.value = sectionEl.dataset.section;
  }
}

function toggleCategory(id) {
  const index = form.category_ids.indexOf(id);
  if (index === -1) {
    form.category_ids.push(id);
  } else {
    form.category_ids.splice(index, 1);
  }
}

function toggleLinkedTechnique(id) {
  const index = form.linked_technique_ids.indexOf(id);
  if (index === -1) {
    form.linked_technique_ids.push(id);
  } else {
    form.linked_technique_ids.splice(index, 1);
  }
}

function toggleLinkedTool(key) {
  const index = form.linked_tool_keys.indexOf(key);
  if (index === -1) {
    form.linked_tool_keys.push(key);
  } else {
    form.linked_tool_keys.splice(index, 1);
  }
}

function addStep() {
  form.steps.push(buildStep());
}

function removeStep(stepIndex) {
  const step = form.steps[stepIndex];
  if (step.id) {
    step._destroy = true;
  } else {
    form.steps.splice(stepIndex, 1);
  }
}

function addGroup(step) {
  step.ingredient_groups.push(buildGroup());
}

function removeGroup(step, groupIndex) {
  const group = step.ingredient_groups[groupIndex];
  if (group.id) {
    group._destroy = true;
  } else {
    step.ingredient_groups.splice(groupIndex, 1);
  }
}

function addIngredient(group) {
  group.ingredients.push(buildIngredient());
}

async function addIngredientAndFocus(group) {
  addIngredient(group);
  await nextTick();
  const lastIngredient = [...group.ingredients]
    .reverse()
    .find((i) => !i._destroy);
  if (lastIngredient) {
    const input = document.querySelector(
      `[data-ingredient-name="${lastIngredient._key}"]`,
    );
    input?.focus();
  }
}

function removeIngredient(group, ingredientIndex) {
  const ingredient = group.ingredients[ingredientIndex];
  if (ingredient.id) {
    ingredient._destroy = true;
  } else {
    group.ingredients.splice(ingredientIndex, 1);
  }
}
</script>
